export const endpoints = {
  auth: {
    me: '/auth/admin/me',
    login: '/auth/admin/login',
    logout: '/auth/admin/logout',
    refresh: '/auth/admin/refresh',
    otpSend: '/auth/admin/login/otp/send',
    otpVerify: '/auth/admin/login/otp/verify',
    otpResend: '/auth/admin/login/otp/resend',
    forgotSend: '/auth/admin/forgot-password/otp/send',
    forgotVerify: '/auth/admin/forgot-password/otp/verify',
    forgotResend: '/auth/admin/forgot-password/otp/resend',
    forgotReset: '/auth/admin/forgot-password/reset',
    activateSetPassword: '/auth/admin/activate/set-password',
  },
  profile: {
    admin: '/profile/admin',
    changePassword: {
      sendOtp: '/profile/admin/change-password/send-otp',
      resendOtp: '/profile/admin/change-password/resend-otp',
      verifyOtp: '/profile/admin/change-password/verify-otp',
      update: '/profile/admin/change-password',
    },
    changePhone: {
      currentSendOtp: '/profile/admin/change-phone/current/send-otp',
      currentResendOtp: '/profile/admin/change-phone/current/resend-otp',
      currentVerifyOtp: '/profile/admin/change-phone/current/verify-otp',
      newSendOtp: '/profile/admin/change-phone/new/send-otp',
      newResendOtp: '/profile/admin/change-phone/new/resend-otp',
      newVerifyOtp: '/profile/admin/change-phone/new/verify-otp',
    },
  },
  products: {
    categories: '/products/admin/categories',
    categoriesExport: '/products/admin/categories/export',
    components: '/products/admin/components',
    componentsExport: '/products/admin/components/export',
    metaCategories: '/products/admin/meta/categories',
    devices: '/products/admin/devices',
    devicesExport: '/products/admin/devices/export',
    metaComponents: '/products/admin/meta/components',
    bundles: '/products/admin/bundles',
    bundlesExport: '/products/admin/bundles/export',
    metaDevices: '/products/admin/meta/devices',
  },
  // Dealer-only, token-scoped controller (the Buyer is always the signed-in Dealer). `export` and
  // `meta` are reserved first segments on the BE controller, so an order id can never be one.
  dealerOrders: {
    list: '/dealer-orders/admin',
    export: '/dealer-orders/admin/export',
    metaCities: '/dealer-orders/admin/meta/cities',
    detail: (id: string) => `/dealer-orders/admin/${id}`,
    invoice: (invoiceId: string) => `/dealer-orders/admin/invoices/${invoiceId}`,
  },
  orders: {
    // Orders is a single resource: collection at `/orders/admin`, detail at `/orders/admin/{id}`.
    // `export`/`meta` are reserved first-segment names, so an order id can never be one of them.
    list: '/orders/admin',
    export: '/orders/admin/export',
    metaCities: '/orders/admin/meta/cities',
    // Shared products-admin meta endpoint (ADO 112770): the orders listing customer filter is
    // populated by `/products/admin/meta/customers` with `hasOrder=true` (customers who have at
    // least one visible order), not an order-scoped route. Same cursor/{id,name} contract.
    metaCustomers: '/products/admin/meta/customers',
    metaAssignees: '/orders/admin/meta/assignees',
    detail: (id: string) => `/orders/admin/${id}`,
    status: (id: string) => `/orders/admin/${id}/status`,
    expectedDelivery: (id: string) => `/orders/admin/${id}/expected-delivery`,
    reviews: (id: string) => `/orders/admin/${id}/reviews`,
    tickets: (id: string) => `/orders/admin/${id}/tickets`,
    /** Order-scoped replacement requests (112365). Returns `{isWithinWindow, requests}`; the
     *  backend owns the replacement window per ADR-0022. */
    replacementRequests: (id: string) => `/orders/admin/${id}/replacement-requests`,
    /** One order-scoped replacement request (112368). No events array — the Activity Timeline is
     *  derived from `requestedAt`/`resolvedAt`/`status` (ADR-0022, FLAG-2). */
    replacementRequestDetail: (orderId: string, requestId: string) =>
      `/orders/admin/${orderId}/replacement-requests/${requestId}`,
    /** Eligible items + their eligible unit numbers for the intake form (112366). One entry per
     *  line item; a bundle arrives as one entry with its component devices absent. */
    replacementEligibleItems: (id: string) => `/orders/admin/${id}/replacement-eligible-items`,
    /** Order-scoped warranty claims (112445). Returns `{hasEligibleDevices, claims}` — the warranty
     *  analogue of the replacement listing's `isWithinWindow`, driving routing, rows and the CTA from
     *  one fetch (ADR-0023). POST on the same path raises a claim (ticket 03). */
    warrantyClaims: (id: string) => `/orders/admin/${id}/warranty-claims`,
    /** One order-scoped warranty claim (112445). Omits `rejectionReason` and `activity[]`, which the
     *  Requests → Warranty queue's endpoint already returns — both are typed tolerant-optional here
     *  (ADR-0023, FLAG-4). */
    warrantyClaimDetail: (orderId: string, claimId: string) =>
      `/orders/admin/${orderId}/warranty-claims/${claimId}`,
    /** Eligible items + their eligible unit numbers for the intake form (112444). Unlike the
     *  replacement equivalent a bundle arrives as **one row per component device**, all sharing one
     *  `orderItemId` (ADR-0023). */
    warrantyEligibleItems: (id: string) => `/orders/admin/${id}/warranty-eligible-items`,
  },
  customers: {
    // Single resource: collection at `/customers/admin`, detail at `/customers/admin/{id}` (GET
    // detail/edit-prefill, PUT, DELETE), status sub-resource at `/customers/admin/{id}/status`.
    // `export` is a reserved first segment, so a customer id can never be one.
    list: '/customers/admin',
    associateDealers: '/customers/admin/meta/associate-dealers',
    export: '/customers/admin/export',
    detail: (id: string) => `/customers/admin/${id}`,
    status: (id: string) => `/customers/admin/${id}/status`,
    activityLog: (id: string) => `/customers/admin/${id}/activity-log`,
    deviceAlerts: (id: string) => `/customers/admin/${id}/device-alerts`,
    deviceAlertsFilters: (id: string) => `/customers/admin/${id}/device-alerts/meta/filters`,
    activityLogExport: (id: string) => `/customers/admin/${id}/activity-log/export`,
    subscriptions: (id: string) => `/customers/admin/${id}/subscriptions`,
    subscriptionsExport: (id: string) => `/customers/admin/${id}/subscriptions/export`,
    // No `/subscriptions/meta` or `/devices/meta` route exists on the backend — the Subscriptions
    // and Devices filter dropdowns are built client-side from the current page's rows instead.
    devices: (id: string) => `/customers/admin/${id}/devices`,
    devicesExport: (id: string) => `/customers/admin/${id}/devices/export`,
    deviceAlertsExport: (id: string) => `/customers/admin/${id}/device-alerts/export`,
    orders: (id: string) => `/customers/admin/${id}/orders`,
    ordersExport: (id: string) => `/customers/admin/${id}/orders/export`,
    ordersDealers: (id: string) => `/customers/admin/${id}/orders/meta/dealers`,
    // Address ZIP lookup (ticket 113129) — `address` is a reserved first segment, never a customer id.
    zipLookup: (zipcode: string) => `/customers/admin/address/zip-lookup?zipcode=${zipcode}`,
    /** Customer Map (feature 113590) — the whole field + device set plus the backend-resolved
     *  Reference location in one call, no paging (ADR-0029). `map` is a reserved sub-path. */
    map: (id: string) => `/customers/admin/${id}/map`,

    /** One Device's live half — metrics, last sync, subscription (feature 113590, BE task 115469).
     *  Its own call, and the only polled one on this surface: the map itself is a load-time
     *  snapshot (`113594` §1f; ADR-0029 amendment). */
    mapDevice: (id: string, deviceId: string) => `/customers/admin/${id}/map/devices/${deviceId}`,
    /** Remap Device page load (feature 119076, BE task 119386 — Closed, live in `/api/docs-json`).
     *  Everything the page needs to open: the Device's identity, its stored position, the Field
     *  Area it belongs to, and how to frame the camera. It deliberately does NOT carry the other
     *  Devices or Field Areas — those are `map` above. */
    deviceRemapContext: (id: string, deviceId: string) =>
      `/customers/admin/${id}/map/devices/${deviceId}/remap-context`,
    /** Every candidate position — a map click (ticket 07) and every coordinate blur (ticket 08) —
     *  goes here, and the answer is a **200 verdict**, never an error (ADR-0031). Nothing is
     *  persisted by it; the write is `PATCH …/location` (ticket 09). `validate` is a reserved
     *  sub-path under `location`, so it can never collide with a device id. */
    deviceLocationValidate: (id: string, deviceId: string) =>
      `/customers/admin/${id}/map/devices/${deviceId}/location/validate`,
    /** The Remap Device page's ONE write (119178 §6a–§6e; BE task 119386). `PATCH`, body
     *  `{ lat, lng }` and **no `origin`** — the wording split `origin` selects belongs to the
     *  verdict route above, and this one composes no copy at all. A save with no change is
     *  permitted and answers `changed: false` (§6b). */
    deviceLocation: (id: string, deviceId: string) =>
      `/customers/admin/${id}/map/devices/${deviceId}/location`,
    /**
     * Admin Field Boundaries (features 113603 + 113611). **Confirmed** by BE on ADO 113604
     * (comment 20945391, 2026-08-21): every field write lives under the existing
     * `…/map/fields` controller, beside the panel GET that already ships. There is
     * deliberately **no list route** — a Customer's Field Areas arrive on `map` above, which is
     * what keeps `deviceCount` consistent on first load. `validate-boundary` and `import` are
     * reserved sub-paths under `fields`, so neither can ever be a field id.
     */
    fields: (id: string) => `/customers/admin/${id}/map/fields`,
    field: (id: string, fieldId: string) => `/customers/admin/${id}/map/fields/${fieldId}`,
    validateBoundary: (id: string) => `/customers/admin/${id}/map/fields/validate-boundary`,
    importFields: (id: string) => `/customers/admin/${id}/map/fields/import`,
  },
  /** Stored invoice PDFs (ADO 117767). Keyed on **`invoiceId`**, not the order — one route serves
   *  both order and subscription invoices, and `orders/admin/:id/invoice` has never existed. The
   *  id travels on the order detail payload as `invoiceId`, null when there is no invoice. */
  invoices: {
    download: (invoiceId: string) => `/invoices/admin/${invoiceId}`,
  },

  /** DM parameter configuration (feature 112235). Deployed under `/devices/admin/*` with `orderId`
   *  as a **query parameter** — the backend models this as a device concern an order can scope, not
   *  an order sub-resource, which is why it is its own group and not nested under `orders`
   *  (ADR-0024). Only `orderDevices` is called today; the rest are the contract for tickets 04–06. */
  deviceParams: {
    orderDevices: (orderId: string) => `/devices/admin/order-devices?orderId=${orderId}`,
    productTypes: '/devices/admin/product-types',
    paramTemplates: (productName: string) =>
      `/devices/admin/param-templates?productName=${encodeURIComponent(productName)}`,
    verifySerial: '/devices/admin/verify-serial',
    // The apply is **two calls** (ticket 13): `set` writes the template and leaves the device in
    // Pending Confirmation, and only `confirm` moves it to Pending QC. The bare `/param-templates`
    // POST these replace was renamed by the backend and no longer exists — the GET on that path is a
    // different endpoint entirely (the template list).
    setParamTemplate: '/devices/admin/param-templates/set',
    confirmParamTemplate: '/devices/admin/param-templates/confirm',
  },
  /** Device Details (feature 102098, story 118861). The devices-admin controller — **not**
   *  customer-scoped (no customer id in the path; grilling Q4) — and its envelope is
   *  `{ message, data }`, which `apiClient.get` unwraps like any `{ data }`. */
  deviceTelemetry: {
    detail: (customerDeviceId: string) => `/devices/admin/${customerDeviceId}`,
    commands: (customerDeviceId: string) => `/devices/admin/${customerDeviceId}/commands`,
    command: (customerDeviceId: string, commandId: string) =>
      `/devices/admin/${customerDeviceId}/commands/${commandId}`,
    history: (customerDeviceId: string) => `/devices/admin/${customerDeviceId}/history`,
    flowRate: (customerDeviceId: string) => `/devices/admin/${customerDeviceId}/flow-rate`,
  },
  discounts: {
    // Single resource: collection at `/discounts/admin`, detail at `/discounts/admin/{id}`,
    // status sub-resource at `/discounts/admin/{id}/status`. `name-availability` is a reserved
    // first-segment sub-path, never a discount id.
    list: '/discounts/admin',
    create: '/discounts/admin',
    nameAvailability: '/discounts/admin/name-availability',
    detail: (id: string) => `/discounts/admin/${id}`,
    status: (id: string) => `/discounts/admin/${id}/status`,
    applicableEntities: (id: string) => `/discounts/admin/${id}/applicable-entities`,
  },
  payments: {
    // Single resource: collection at `/payments/admin`, per-entry timeline at
    // `/payments/admin/{paymentId}/timeline`. `export` is a reserved first segment.
    list: '/payments/admin',
    export: '/payments/admin/export',
    timeline: (paymentId: string) => `/payments/admin/${paymentId}/timeline`,
  },
  discountsMeta: {
    customers: '/products/admin/meta/customers',
    bundles: '/products/admin/meta/bundles',
    // 109605 resolved: BE extended the shared endpoint with `includeComponents` (returns the
    // unified device+component list with an `isComponent` flag) rather than a separate route.
    devices: '/products/admin/meta/devices',
  },
  requests: {
    // Requests → Installations queue. **There is no `GET /requests/admin/installations`** — the
    // collection route does not exist on the backend (verified against `greenway_api` on `qa` and
    // `develop`, ADO 120855; a constant naming it was removed there after sitting unused). Listing
    // is `POST .../query` with a JSON filter body (114698).
    query: '/requests/admin/installations/query',
    export: '/requests/admin/installations/export',
    // Keyed by orderId (one row per order) — there is no separate installation-request id.
    detail: (orderId: string) => `/requests/admin/installations/${orderId}`,
    approve: (orderId: string) => `/requests/admin/installations/${orderId}/approve`,
    reject: (orderId: string) => `/requests/admin/installations/${orderId}/reject`,
    // Shared active-field-installer directory (114698): flat, unpaginated, optional `search`.
    // Serves both the Installations filter drawer's Assignee options (no search) and the
    // approve-modal installer typeahead (search as typed).
    metaFieldInstallers: '/requests/admin/meta/field-installers',
    // Requests → Replacements queue (ADR-0019). As with Installations above, **there is no
    // `GET /requests/admin/replacements`**: listing is `POST .../query` (ADO 120855).
    replacements: {
      query: '/requests/admin/replacements/query',
      detail: (id: string) => `/requests/admin/replacements/${id}`,
      reject: (id: string) => `/requests/admin/replacements/${id}/reject`,
      approve: (id: string) => `/requests/admin/replacements/${id}/approve`,
      export: '/requests/admin/replacements/export',
      // Filter-option meta (114698, BE-confirmed on story 110171): Customer/Device/Device
      // Category reuse the existing products-admin meta endpoints (same cursor/{id,name}
      // contract Orders already consumes); City reuses the existing orders-admin meta endpoint.
      // Field-installers is the replacements-specific sibling of `requests.metaFieldInstallers`
      // (own controller method, same flat/unpaginated shape).
      meta: {
        customers: '/products/admin/meta/customers',
        devices: '/products/admin/meta/devices',
        cities: '/orders/admin/meta/cities',
        categories: '/products/admin/meta/categories',
        fieldInstallers: '/requests/admin/replacements/meta/field-installers',
      },
    },
    // Requests → Warranty queue (stories 110178/110179; real contract wired on 115394). Listing is
    // `POST .../query` with a JSON filter body, like both sibling queues; decisions are `PATCH`.
    // Filter-option meta reuses the existing product/order meta endpoints, so nothing meta-shaped
    // here.
    warranty: {
      query: '/requests/admin/warranty-claims/query',
      detail: (id: string) => `/requests/admin/warranty-claims/${id}`,
      approve: (id: string) => `/requests/admin/warranty-claims/${id}/approve`,
      reject: (id: string) => `/requests/admin/warranty-claims/${id}/reject`,
      export: '/requests/admin/warranty-claims/export',
      // Filter-option meta reuses the existing products-admin endpoints, the same ones the
      // Replacements sibling consumes — named here rather than borrowed from another feature's
      // hook, because the scoping params are what make them different calls (ADO 118533).
      meta: {
        devices: '/products/admin/meta/devices',
      },
    },
  },
  storage: {
    presignedUrl: '/storage/admin/pre-signed-url',
  },
  adminUsers: {
    list: '/internal-users/admin',
    create: '/internal-users/admin',
    detail: (id: string) => `/internal-users/admin/${id}`,
    status: (id: string) => `/internal-users/admin/${id}/status`,
    export: '/internal-users/admin/export',
  },
  dealers: {
    list: '/dealers/admin',
    create: '/dealers/admin',
    usCities: '/dealers/admin/meta/us-cities',
    detail: (id: string) => `/dealers/admin/${id}`,
    status: (id: string) => `/dealers/admin/${id}/status`,
    metaCities: '/dealers/admin/meta/cities',
    /** One allocation per call (`allocation` query param, required). */
    devices: (id: string) => `/dealers/admin/${id}/devices`,
    devicesExport: (id: string) => `/dealers/admin/${id}/devices/export`,
    deviceFilters: (id: string) => `/dealers/admin/${id}/devices/meta/filters`,
  },
  // The dealer's own devices (feature 122138) — token-scoped, dealer role only.
  dealerDevices: {
    list: '/dealer-devices/admin',
    assignable: '/dealer-devices/admin/meta/assignable',
    assign: '/dealer-devices/admin/assign',
    filters: '/dealer-devices/admin/meta/filters',
    export: '/dealer-devices/admin/export',
  },
  dealerCustomers: { options: '/dealer-customers/admin/meta/options' },
  dashboard: {
    // The cards row is one call powering all five tiles, which is why loading/error/retry are
    // row-level and not per tile (ADR-0027 §5). Tickets 06-08 add the three graphs and the
    // quick-action counts alongside it.
    cards: '/dashboard/admin/cards',
    // Its own region, its own filter, its own skeleton/error/retry. One point per calendar day
    // whatever the period — `labelGranularity` in the response labels the x-axis and never
    // aggregates (ADR-0027 §3).
    customerOnboarding: '/dashboard/admin/graphs/customer-onboarding',
    // The only graph that AGGREGATES: here `labelGranularity` sets the bucket size as well as the
    // label, and the backend pre-formats every label and clamps every partial edge bucket to the
    // window. The client renders what it is given (ADR-0027 §1, CONTEXT.md "Label granularity").
    devicesBundlesSold: '/dashboard/admin/graphs/devices-bundles-sold',
    /** Open-right-now counts for the three quick-action tabs. Takes **no** window parameter —
     *  no filter on the Dashboard reaches it (ADR-0027 §6). */
    quickActionCounts: '/dashboard/admin/quick-actions/counts',
    // Its own region, same envelope as the onboarding graph with two named counts per day instead
    // of one. Both counts are hardcoded 0 in the backend until the support module ships — that is
    // correct data, not a gap, and nothing on the client branches on it.
    supportTickets: '/dashboard/admin/graphs/support-tickets',
  },
  /** Reports (feature 112255). Four read-only offset-paged listings plus a `/export` twin each,
   *  taking the same params minus `page`/`limit` so an export always covers the whole matching set.
   *  The contract is FE-proposed, published in the Postman `Sprint 5 - Stubs → Admin → Reports`
   *  folder and **not yet confirmed by BE** (comment 20842911 on 112312) — a mismatch surfaces as a
   *  `contract_drift` Sentry error via `parseContract`, not a silent empty grid. `export` is a
   *  reserved first segment, and Reports has no `/:id` route at all (a report is a lens, never a
   *  detail page), so there is nothing for it to collide with. Flat `<tab>` / `<tab>Export` pairs so
   *  all four tabs read the same way. */
  reports: {
    deviceActivity: '/reports/admin/device-activity',
    deviceActivityExport: '/reports/admin/device-activity/export',
    orderSummary: '/reports/admin/order-summary',
    orderSummaryExport: '/reports/admin/order-summary/export',
    products: '/reports/admin/products',
    productsExport: '/reports/admin/products/export',
    subscriptions: '/reports/admin/subscriptions',
    subscriptionsExport: '/reports/admin/subscriptions/export',
    /** Filter-option meta. There are **no** `/reports/admin/meta/*` routes: all three dropdowns
     *  reuse products-admin meta endpoints that already ship, narrowed by a boolean the backend
     *  added for Reports — `hasProduct` on customers, `isOwned` on devices and categories. Unlike
     *  the FE-proposed report grids above, **these three are deployed and verified** against
     *  `greenway_api@develop`'s DTOs. Cursor-paged (`{ limit, cursor, search }` →
     *  `{ data, meta: { nextCursor?, hasNextPage } }`), not offset-paged like the grids. */
    meta: {
      customers: '/products/admin/meta/customers',
      devices: '/products/admin/meta/devices',
      categories: '/products/admin/meta/categories',
    },
  },
} as const;
